import React from 'react';
import { Calendar, Clock, User, Star, CheckCircle, XCircle, ArrowRight, AlertTriangle, TrendingDown, TrendingUp, Zap, Shield, Scale, Brain, FileText, Building2, HelpCircle, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SEOHead } from '@/components/SEOHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';

export const ClaudeCoworkLegalPage = () => {
  const { language } = useLanguage();

  const faqItems = language === 'fr' ? [
    {
      question: "Qu'est-ce que Claude Cowork Legal ?",
      answer: "Claude Cowork Legal est un plugin juridique open source développé par Anthropic pour sa plateforme Claude Cowork. Lancé le 30 janvier 2026, il permet aux directions juridiques d'entreprise d'automatiser la revue de contrats, le triage de NDA, la vérification de fournisseurs et la génération de briefings juridiques. Il fonctionne localement sur macOS dans un environnement sécurisé et nécessite un abonnement Claude Max (100-200$/mois)."
    },
    {
      question: "Pourquoi Claude Cowork a-t-il fait chuter la Bourse de 285 milliards de dollars ?",
      answer: "Le 3 février 2026, Thomson Reuters a chuté de 16%, Wolters Kluwer de 10% et RELX de 9%, soit 285 milliards de dollars de capitalisation boursière perdus (sources : Bloomberg, Reuters, The Guardian). La panique n'est pas liée au produit lui-même mais au signal stratégique : pour la première fois, un développeur de modèle fondamental (Anthropic) entre directement sur le marché des éditeurs juridiques, menaçant le modèle de licence par siège à 500-1500$/mois."
    },
    {
      question: "Claude Cowork Legal peut-il remplacer Harvey, Lexis AI ou Jimini AI ?",
      answer: "Non, pas dans son état actuel (février 2026). Claude Cowork Legal n'intègre pas de base de jurisprudence vérifiable, ne fonctionne que sur macOS, et ses workflows multi-étapes sont encore instables. Harvey, Jimini AI et Lexis AI+ disposent de bases de données juridiques authentifiées et sont prêts pour la production. Le signal stratégique est à 5/5 mais le produit est à 2/5."
    },
    {
      question: "Claude Cowork est-il adapté aux avocats français ?",
      answer: "Non, Claude Cowork Legal n'est pas adapté au droit français en l'état. Il n'accède pas à la jurisprudence de la Cour de cassation ou du Conseil d'État et ne connaît pas les spécificités du droit continental. Il cible les directions juridiques d'entreprise anglo-saxonnes (in-house counsel). Les solutions comme Jimini AI ou Lexis AI+ restent largement supérieures pour la pratique du droit français."
    },
    {
      question: "Combien coûte Claude Cowork Legal ?",
      answer: "Claude Cowork Legal nécessite un abonnement Claude Max qui coûte entre 100 et 200 dollars par mois (environ 90-180€). C'est significativement moins cher que les solutions juridiques spécialisées comme Harvey (tarification enterprise, souvent supérieure à 1 500$/siège/mois). Cependant, le rapport qualité/fonctionnalités n'est pas comparable car Claude Cowork n'inclut pas de base de données juridique."
    },
    {
      question: "Quelles sont les fonctionnalités du plugin juridique Claude Cowork ?",
      answer: "Le plugin juridique Claude Cowork propose 5 fonctionnalités principales : /review-contract (revue clause par clause contre un playbook configurable), /triage-nda (tri automatique des NDA entrants), /vendor-check (vérification des contrats fournisseurs), /respond (réponses types pour DSAR et discovery holds), et /brief (briefings contextuels automatisés). Tous les outputs doivent être vérifiés par un avocat selon Anthropic."
    },
    {
      question: "Le buzz autour de Claude Cowork Legal était-il justifié ?",
      answer: "Le buzz était partiellement justifié. Le signal stratégique est majeur : Anthropic a rompu la chaîne de valeur traditionnelle en entrant directement sur le marché applicatif juridique, créant un précédent qui va restructurer l'industrie. Cependant, la panique boursière de 285 milliards de dollars était disproportionnée car le produit lui-même est encore immature (macOS uniquement, pas de base juridique, workflows instables). Notre verdict : signal 5/5, produit 2/5, note globale 3/5."
    },
    {
      question: "Qu'est-ce que le Model Context Protocol (MCP) de Claude Cowork ?",
      answer: "Le Model Context Protocol (MCP) est l'infrastructure ouverte qui permet à Claude Cowork de se connecter à des outils tiers comme Slack, Box, Egnyte, Jira et Microsoft 365. Pour les directions juridiques, cette connectivité native signifie que l'IA peut accéder directement aux documents et communications de l'entreprise sans migration de données. Les plugins MCP sont open source et publiés sur GitHub."
    }
  ] : [
    {
      question: "What is Claude Cowork Legal?",
      answer: "Claude Cowork Legal is an open-source legal plugin developed by Anthropic for its Claude Cowork platform. Launched on January 30, 2026, it enables in-house legal teams to automate contract review, NDA triage, vendor checks, and legal briefings. It runs locally on macOS in a sandboxed environment and requires a Claude Max subscription ($100-200/month)."
    },
    {
      question: "Why did Claude Cowork cause a $285 billion stock market crash?",
      answer: "On February 3, 2026, Thomson Reuters fell 16%, Wolters Kluwer 10%, and RELX 9%, wiping out $285 billion in market cap (sources: Bloomberg, Reuters, The Guardian). The panic was not about the product itself but the strategic signal: for the first time, a foundation model developer (Anthropic) entered the legal tech application layer directly, threatening the $500-1,500/seat/month licensing model of incumbent vendors."
    },
    {
      question: "Can Claude Cowork Legal replace Harvey, Lexis AI, or Jimini AI?",
      answer: "No, not in its current state (February 2026). Claude Cowork Legal lacks integrated legal databases, only works on macOS, and its multi-step workflows are still unstable. Harvey, Jimini AI, and Lexis AI+ have authenticated legal databases and are production-ready. The strategic signal rates 5/5 but the product itself is 2/5."
    },
    {
      question: "How much does Claude Cowork Legal cost?",
      answer: "Claude Cowork Legal requires a Claude Max subscription costing $100-200 per month. This is significantly cheaper than specialized legal AI solutions like Harvey (enterprise pricing, often above $1,500/seat/month). However, the value comparison is not straightforward since Claude Cowork doesn't include legal databases."
    },
    {
      question: "What features does the Claude Cowork legal plugin offer?",
      answer: "The Claude Cowork legal plugin offers 5 main features: /review-contract (clause-by-clause review against configurable playbooks), /triage-nda (automatic NDA triage), /vendor-check (vendor agreement status checks), /respond (templated responses for DSARs and discovery holds), and /brief (automated contextual briefings). All outputs must be reviewed by licensed attorneys per Anthropic's guidance."
    },
    {
      question: "Was the Claude Cowork Legal hype justified?",
      answer: "The hype was partially justified. The strategic signal is major: Anthropic broke the traditional value chain by entering the legal application market directly, setting a precedent that will restructure the industry. However, the $285 billion market panic was disproportionate because the product itself is still immature (macOS only, no legal database, unstable workflows). Our verdict: signal 5/5, product 2/5, overall 3/5."
    },
    {
      question: "What is the Model Context Protocol (MCP) in Claude Cowork?",
      answer: "The Model Context Protocol (MCP) is the open infrastructure that allows Claude Cowork to connect to third-party tools like Slack, Box, Egnyte, Jira, and Microsoft 365. For legal departments, this native connectivity means AI can directly access enterprise documents and communications without data migration. MCP plugins are open source and published on GitHub."
    }
  ];

  const structuredData = language === 'fr' ? [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Claude Cowork Legal : Analyse Complète du Plugin Juridique qui a Fait Trembler la Bourse — 285 Milliards de Dollars Évaporés",
      "alternativeHeadline": "Claude Cowork Juridique : Pourquoi Thomson Reuters a Chuté de 16% et Ce Que Ça Change pour les Avocats",
      "description": "Analyse approfondie du plugin juridique Claude Cowork d'Anthropic. Impact boursier de 285 milliards de dollars sur Thomson Reuters (-16%), RELX (-9%), Wolters Kluwer (-10%). Comparatif avec Harvey, Jimini AI, Lexis AI+. Verdict : signal 5/5, produit 2/5.",
      "datePublished": "2026-02-18T10:00:00+01:00",
      "dateModified": "2026-02-18T10:00:00+01:00",
      "wordCount": 4500,
      "author": {
        "@type": "Organization",
        "name": "Gaius Legal AI",
        "url": "https://gaius-tech.com",
        "description": "Cabinet de formation et conseil en intelligence artificielle juridique pour avocats et juristes"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Gaius Legal AI",
        "url": "https://gaius-tech.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://gaius-tech.com/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://gaius-tech.com/articles/claude-cowork-legal"
      },
      "about": [
        { "@type": "Thing", "name": "Claude Cowork" },
        { "@type": "Thing", "name": "Anthropic" },
        { "@type": "Thing", "name": "Legal Technology" },
        { "@type": "Thing", "name": "Thomson Reuters" },
        { "@type": "Thing", "name": "Intelligence Artificielle Juridique" }
      ],
      "citation": [
        { "@type": "Article", "name": "Anthropic AI Tool Sparks Selloff From Software to Broader Market", "publisher": { "@type": "Organization", "name": "Bloomberg" }, "datePublished": "2026-02-03" },
        { "@type": "Article", "name": "Anthropic's new AI tools deepen selloff in data analytics and software stocks", "publisher": { "@type": "Organization", "name": "Reuters" }, "datePublished": "2026-02-03" },
        { "@type": "Article", "name": "Anthropic's Launch of AI Legal Tool Hits Shares in European Data Companies", "publisher": { "@type": "Organization", "name": "The Guardian" }, "datePublished": "2026-02-03" },
        { "@type": "Article", "name": "Thomson Reuters, RELX, and Wolters Stocks Crushed After Anthropic Debuts Claude Legal Plug-In", "publisher": { "@type": "Organization", "name": "Morningstar" }, "datePublished": "2026-02-04" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    }
  ] : [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Claude Cowork Legal: Complete Analysis of the Anthropic Plugin That Wiped $285 Billion Off the Markets",
      "alternativeHeadline": "Claude Cowork Legal Review: Why Thomson Reuters Fell 16% and What It Means for Lawyers",
      "description": "In-depth analysis of Anthropic's Claude Cowork legal plugin. $285 billion market impact on Thomson Reuters (-16%), RELX (-9%), Wolters Kluwer (-10%). Comparison with Harvey, Jimini AI, Lexis AI+. Verdict: signal 5/5, product 2/5.",
      "datePublished": "2026-02-18T10:00:00+01:00",
      "dateModified": "2026-02-18T10:00:00+01:00",
      "wordCount": 4500,
      "author": {
        "@type": "Organization",
        "name": "Gaius Legal AI",
        "url": "https://gaius-tech.com",
        "description": "Legal AI training and consulting firm for lawyers and legal professionals"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Gaius Legal AI",
        "url": "https://gaius-tech.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://gaius-tech.com/lovable-uploads/1b5b9971-7f8c-4d09-a61e-d40ffa00ed01.png"
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": "https://gaius-tech.com/articles/claude-cowork-legal"
      },
      "about": [
        { "@type": "Thing", "name": "Claude Cowork" },
        { "@type": "Thing", "name": "Anthropic" },
        { "@type": "Thing", "name": "Legal Technology" },
        { "@type": "Thing", "name": "Thomson Reuters" }
      ],
      "citation": [
        { "@type": "Article", "name": "Anthropic AI Tool Sparks Selloff From Software to Broader Market", "publisher": { "@type": "Organization", "name": "Bloomberg" }, "datePublished": "2026-02-03" },
        { "@type": "Article", "name": "Anthropic's new AI tools deepen selloff in data analytics and software stocks", "publisher": { "@type": "Organization", "name": "Reuters" }, "datePublished": "2026-02-03" },
        { "@type": "Article", "name": "Anthropic's Launch of AI Legal Tool Hits Shares in European Data Companies", "publisher": { "@type": "Organization", "name": "The Guardian" }, "datePublished": "2026-02-03" }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(item => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer
        }
      }))
    }
  ];

  const seoTitle = language === 'fr'
    ? "Claude Cowork Legal : analyse complète 2026"
    : "Claude Cowork Legal: Full 2026 Review";

  const seoDescription = language === 'fr'
    ? "Qu'est-ce que Claude Cowork Legal ? Analyse du plugin juridique d'Anthropic qui a fait perdre 285 milliards $ aux marchés. Thomson Reuters -16%. Comparatif Harvey, Jimini, Lexis. Verdict expert : signal 5/5, produit 2/5."
    : "What is Claude Cowork Legal? Analysis of Anthropic's legal plugin that wiped $285B off markets. Thomson Reuters -16%. Comparison with Harvey, Jimini, Lexis. Expert verdict: signal 5/5, product 2/5.";

  // Pass structured data as array - SEOHead needs to handle it
  const structuredDataCombined = structuredData;

  if (language === 'fr') {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={seoTitle}
          description={seoDescription}
          keywords="claude cowork juridique, claude cowork legal, qu'est-ce que claude cowork, anthropic plugin juridique, claude cowork avocat, claude cowork avis, claude cowork prix, claude cowork fonctionnalités, ia juridique anthropic, claude cowork analyse, claude cowork vs harvey, claude cowork vs jimini, claude cowork vs lexis, cowork legal plugin, anthropic legal, claude cowork bourse, thomson reuters claude, ia avocats 2026, claude cowork test, claude cowork review, mcp claude cowork"
          canonicalUrl="https://gaius-tech.com/articles/claude-cowork-legal"
          alternateUrlFr="https://gaius-tech.com/articles/claude-cowork-juridique"
          alternateUrlEn="https://gaius-tech.com/articles/claude-cowork-legal"
          structuredData={structuredDataCombined}
          pageType="article"
        />

        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">Analyse Expert</Badge>
                <Badge variant="secondary" className="bg-red-100 text-red-800 flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  -285 Md$ en Bourse
                </Badge>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">Février 2026</Badge>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Claude Cowork Legal : le plugin juridique d'Anthropic qui a fait perdre 285 milliards de dollars aux marchés
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Thomson Reuters : -16% (≈75 Md$ de capitalisation perdus). Wolters Kluwer : -10%. RELX : -9%. Le buzz était-il justifié ? Notre analyse complète par des experts en IA juridique.
              </p>
              <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>18 février 2026</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>25 min de lecture</span>
                </span>
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>Équipe Gaius — Formation IA Juridique</span>
                </span>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&h=400&fit=crop"
              alt="Claude Cowork Legal - Analyse de l'impact boursier de 285 milliards de dollars sur Thomson Reuters, RELX et Wolters Kluwer"
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Quick Answer Box - optimized for AI extraction */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
            <div className="flex items-start gap-3 mb-3">
              <MessageSquare className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
              <h2 className="font-bold text-lg text-foreground">En bref : Qu'est-ce que Claude Cowork Legal ?</h2>
            </div>
            <p className="text-foreground/80 mb-3">
              <strong>Claude Cowork Legal</strong> est un plugin juridique open source lancé le 30 janvier 2026 par <strong>Anthropic</strong> pour sa plateforme Claude Cowork. Il automatise la revue de contrats, le triage de NDA et la vérification de fournisseurs pour les directions juridiques d'entreprise. Son lancement a provoqué une <strong>chute boursière de 285 milliards de dollars</strong> (Thomson Reuters -16%, source : Bloomberg, 3 février 2026). <strong>Notre verdict : signal stratégique 5/5, produit actuel 2/5, note globale 3/5.</strong>
            </p>
            <p className="text-sm text-muted-foreground">
              Analyse par <a href="https://gaius-tech.com" className="text-primary hover:underline">Gaius Legal AI</a> — Cabinet de formation et conseil en IA juridique pour avocats et juristes.
            </p>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <details className="bg-secondary/30 rounded-xl p-6" open>
            <summary className="font-bold text-foreground cursor-pointer">Sommaire de l'article</summary>
            <nav className="mt-4 space-y-2 text-sm">
              <a href="#quest-ce-que-claude-cowork" className="block text-primary hover:underline">1. Qu'est-ce que Claude Cowork ? La plateforme derrière le plugin</a>
              <a href="#fonctionnalites-plugin" className="block text-primary hover:underline">2. Quelles sont les fonctionnalités du plugin juridique ?</a>
              <a href="#pourquoi-buzz" className="block text-primary hover:underline">3. Pourquoi Claude Cowork a-t-il fait autant de buzz ?</a>
              <a href="#impact-boursier" className="block text-primary hover:underline">4. L'impact boursier en chiffres : 285 milliards de dollars perdus</a>
              <a href="#buzz-justifie" className="block text-primary hover:underline">5. Le buzz était-il justifié ? Notre verdict d'experts</a>
              <a href="#comparatif" className="block text-primary hover:underline">6. Claude Cowork vs Harvey vs Jimini AI vs Lexis AI+ : le comparatif</a>
              <a href="#avocats-francais" className="block text-primary hover:underline">7. Claude Cowork est-il adapté aux avocats français ?</a>
              <a href="#recommandation" className="block text-primary hover:underline">8. Notre recommandation : comment réagir en 2026</a>
              <a href="#faq" className="block text-primary hover:underline">9. Questions fréquentes sur Claude Cowork Legal</a>
            </nav>
          </details>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">

            <p className="text-xl leading-relaxed mb-8">
              Le 30 janvier 2026, <strong>Anthropic</strong> — la société derrière le modèle <strong>Claude</strong> — a lancé un plugin juridique pour sa nouvelle plateforme <strong>Claude Cowork</strong>. Ce qui aurait pu passer pour une simple mise à jour produit a déclenché un séisme boursier sans précédent dans l'industrie de l'information juridique. Selon <strong>Bloomberg</strong> (3 février 2026), <strong>Thomson Reuters a chuté de 16%, Wolters Kluwer de 10%, RELX de 9%</strong>. En une semaine, plus de <strong>285 milliards de dollars de capitalisation boursière</strong> se sont évaporés. Que s'est-il réellement passé ? Et surtout : le buzz était-il justifié ?
            </p>

            {/* Key Impact Box */}
            <div className="bg-red-50 border-l-4 border-red-400 p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <TrendingDown className="w-6 h-6 text-red-500 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-red-800 font-bold text-lg mb-2">L'impact boursier en chiffres concrets</h4>
                  <ul className="text-red-700 space-y-1 mb-0">
                    <li>• <strong>Thomson Reuters (TRI)</strong> : -16,03% le 3 février 2026 (≈ 75 Md$ de capitalisation perdus en une séance)</li>
                    <li>• <strong>Wolters Kluwer (WKL)</strong> : -10,2% sur Euronext Amsterdam</li>
                    <li>• <strong>RELX (REL)</strong> : -9,1% sur le London Stock Exchange</li>
                    <li>• <strong>Pearson, Sage Group</strong> et autres éditeurs : -5% à -8%</li>
                    <li>• <strong>Total estimé</strong> : 285 milliards de dollars de perte de capitalisation cumulée en 5 jours</li>
                  </ul>
                  <p className="text-red-600 text-sm mt-3 mb-0">
                    <em>Sources : Bloomberg, « Anthropic AI Tool Sparks Selloff From Software to Broader Market » ; Reuters, « Anthropic's new AI tools deepen selloff in data analytics and software stocks » ; The Guardian, « Anthropic's Launch of AI Legal Tool Hits Shares in European Data Companies » — 3 février 2026. Morningstar, « Thomson Reuters, RELX, and Wolters Stocks Crushed After Anthropic Debuts Claude Legal Plug-In » — 4 février 2026.</em>
                  </p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 id="quest-ce-que-claude-cowork" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Qu'est-ce que Claude Cowork ? Comprendre la plateforme avant le plugin
            </h2>

            <p className="mb-6">
              Pour comprendre le buzz, il faut d'abord comprendre <strong>Claude Cowork</strong>. Lancé le 12 janvier 2026, Cowork est une extension de <strong>Claude Code</strong>, l'outil de codage agentique d'Anthropic. Si Claude Code est conçu pour les développeurs, Cowork s'adresse à tous les professionnels : il se positionne comme <em>« Claude Code pour le reste de votre travail »</em> (source : <strong>annonce officielle d'Anthropic</strong>, 12 janvier 2026).
            </p>

            <p className="mb-6">
              Concrètement, Cowork fonctionne sur votre ordinateur local. Vous lui donnez accès à un dossier, et il peut <strong>lire, éditer et créer des fichiers</strong> dans cet espace de travail. L'interface est conçue non pas comme un chat, mais comme un système d'attribution de tâches à un collègue virtuel. L'agent planifie, exécute et itère sur des workflows multi-étapes de manière autonome, dans un environnement de machine virtuelle sécurisé (sandbox).
            </p>

            <p className="mb-8">
              C'est un changement de paradigme par rapport à l'utilisation classique de Claude en conversation : ici, l'IA ne se contente pas de répondre à des questions, elle <strong>agit</strong>. C'est ce qu'on appelle le passage du <strong>modèle conversationnel au modèle agentique</strong>.
            </p>

            <div className="bg-primary/5 border-l-4 border-primary p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <Brain className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-bold text-lg mb-2">Claude Cowork : fiche technique complète</h4>
                  <ul className="text-foreground/80 space-y-1 mb-0">
                    <li>• <strong>Type</strong> : IA agentique (planification + exécution autonome de tâches)</li>
                    <li>• <strong>Lancement</strong> : 12 janvier 2026 (plateforme) / 30 janvier 2026 (plugin juridique)</li>
                    <li>• <strong>Environnement</strong> : local, dans une machine virtuelle sécurisée (sandbox)</li>
                    <li>• <strong>Prérequis</strong> : abonnement Claude Max (100-200$/mois, soit environ 90-180€/mois)</li>
                    <li>• <strong>Plateforme</strong> : macOS uniquement (Windows prévu mi-2026 selon Anthropic)</li>
                    <li>• <strong>Connecteurs MCP</strong> : Slack, Box, Egnyte, Jira, Microsoft 365</li>
                    <li>• <strong>Open source</strong> : plugins publiés sur GitHub sous forme de templates personnalisables</li>
                    <li>• <strong>Développeur</strong> : Anthropic (San Francisco, fondé par Dario et Daniela Amodei, ex-OpenAI)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <h2 id="fonctionnalites-plugin" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Quelles sont les fonctionnalités du plugin juridique Claude Cowork ?
            </h2>

            <p className="mb-6">
              Le 30 janvier 2026, Anthropic a publié sur GitHub une suite de <strong>11 plugins « starter »</strong> pour Cowork, couvrant le marketing, la finance, le support client, les ventes — et le <strong>juridique</strong>. Ces plugins sont <strong>open source</strong>, conçus comme des modèles que les entreprises peuvent personnaliser et adapter à leurs besoins spécifiques.
            </p>

            <p className="mb-6">
              Le plugin juridique cible spécifiquement les <strong>directions juridiques d'entreprise</strong> (<em>in-house counsel</em>) avec les fonctionnalités suivantes :
            </p>

            <div className="grid md:grid-cols-2 gap-4 my-8">
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <FileText className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">/review-contract</h4>
                      <p className="text-sm text-muted-foreground">Revue clause par clause contre un playbook de négociation configurable, avec drapeaux vert/jaune/rouge et suggestions de redline. Comparable aux fonctionnalités de contract review de Harvey ou Kira Systems.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">/triage-nda</h4>
                      <p className="text-sm text-muted-foreground">Tri automatique des NDA entrants en 3 catégories : approbation standard, revue par un juriste, ou examen complet. Réduit le temps de traitement de NDA estimé à 80% selon les premiers tests.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">/vendor-check</h4>
                      <p className="text-sm text-muted-foreground">Vérification du statut des contrats fournisseurs sur l'ensemble du portefeuille. Identifie les échéances, clauses de renouvellement automatique et conditions atypiques.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Zap className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-foreground">/respond & /brief</h4>
                      <p className="text-sm text-muted-foreground">Réponses types (DSAR/RGPD, discovery holds, demandes internes) et briefings contextuels automatisés à partir des documents du workspace.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <p className="mb-8">
              Anthropic encadre explicitement le plugin avec un avertissement clair : <em>« All outputs should be reviewed by licensed attorneys »</em> (source : README GitHub du plugin, 30 janvier 2026). Le plugin est présenté comme une <strong>assistance au travail juridique</strong>, pas un conseil juridique. Une distinction fondamentale dans le cadre déontologique de la profession d'avocat.
            </p>

            {/* Section 3 */}
            <h2 id="pourquoi-buzz" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Pourquoi Claude Cowork a-t-il fait autant de buzz ? Les 4 facteurs clés
            </h2>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              Facteur n°1 : Le passage du modèle fondamental à la couche applicative
            </h3>

            <p className="mb-6">
              C'est <strong>le facteur déterminant</strong>. Jusqu'ici, les développeurs de modèles fondamentaux (Anthropic, OpenAI, Google DeepMind) restaient dans leur rôle de <strong>fournisseurs d'infrastructure</strong>. Les éditeurs juridiques (Thomson Reuters avec Westlaw, LexisNexis avec Lexis+, Wolters Kluwer) construisaient des produits <em>par-dessus</em> ces modèles. L'équation était simple : <strong>modèle + wrapper + workflow = produit juridique</strong>.
            </p>

            <p className="mb-8">
              En lançant son propre plugin juridique, Anthropic a <strong>court-circuité les intermédiaires</strong>. Pour la première fois, un développeur de modèle fondamental packagait un produit de workflow juridique directement dans sa plateforme. Comme l'a écrit <strong>Robert Ambrogi sur LawNext</strong> (février 2026) : <em>« This may be the opening salvo in a war that reshapes the legal tech industry »</em>. C'est cette rupture structurelle qui a paniqué les marchés, bien plus que les fonctionnalités du plugin lui-même.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              Facteur n°2 : La frustration tarifaire accumulée dans l'industrie
            </h3>

            <p className="mb-8">
              Les outils d'IA juridique spécialisés pratiquent des tarifs élevés. Selon les données du marché, les solutions enterprise comme <strong>Harvey facturent plus de 1 500 $/siège/mois</strong> pour les grands cabinets. Même les solutions mid-market se situent entre <strong>500 et 1 000 $/utilisateur/mois</strong>. La perspective qu'un modèle fondamental puisse offrir des fonctionnalités juridiques à <strong>100-200$/mois</strong> (prix de Claude Max) a été perçue comme une <strong>correction tarifaire inévitable</strong>. L'annonce a cristallisé une frustration latente du marché que l'analyste de Morningstar <strong>Ali Mogharabi</strong> a qualifiée de <em>« pricing pressure that had been building for years »</em>.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              Facteur n°3 : L'effet de distribution multi-départements
            </h3>

            <p className="mb-8">
              Le plugin juridique n'est pas isolé : il fait partie d'une suite de <strong>11 plugins</strong> couvrant les ventes, le marketing, la finance et le support client. Pour les directions juridiques qui gèrent des NDA provenant du commercial ou des contrats fournisseurs de l'achat, cette <strong>intégration transversale</strong> pourrait constituer un avantage concurrentiel significatif par rapport aux outils juridiques cloisonnés. C'est ce que le cabinet <strong>Reed Smith</strong> a souligné dans son analyse du 16 février 2026 : <em>« The cross-departmental integration is what makes this genuinely different from existing legal tech »</em>.
            </p>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              Facteur n°4 : L'open source comme accélérateur de disruption
            </h3>

            <p className="mb-8">
              Les plugins sont <strong>publiés sur GitHub</strong> sous forme de templates personnalisables. Les équipes legal ops peuvent les adapter sans attendre qu'un éditeur développe la fonctionnalité. C'est un modèle de distribution radicalement différent du SaaS classique, et il menace frontalement le modèle de licence par siège des éditeurs établis. Selon <strong>Artificial Lawyer</strong> (17 février 2026), <em>« The open-source approach could become a Trojan horse for enterprise legal departments »</em>.
            </p>

            {/* Section 4 */}
            <h2 id="impact-boursier" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              L'impact boursier détaillé : comment 285 milliards de dollars se sont évaporés en 5 jours
            </h2>

            <p className="mb-6">
              La chronologie des événements illustre la rapidité de la contagion :
            </p>

            <div className="bg-secondary/30 rounded-xl p-6 my-8">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="font-mono font-bold text-primary whitespace-nowrap">30 janv.</span>
                  <p className="mb-0">Anthropic publie les 11 plugins Cowork sur GitHub, dont le plugin juridique.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-mono font-bold text-primary whitespace-nowrap">31 janv.</span>
                  <p className="mb-0">Les premiers articles d'analyse paraissent dans la presse tech. Le plugin juridique attire l'attention des analystes financiers.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-mono font-bold text-red-600 whitespace-nowrap">3 févr.</span>
                  <p className="mb-0"><strong>Journée noire</strong>. Thomson Reuters ouvre en baisse de 12% et finit à -16,03%. Wolters Kluwer perd 10,2% sur Euronext. RELX chute de 9,1% à Londres. Bloomberg, Reuters et The Guardian publient des analyses de la vente massive.</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-mono font-bold text-red-600 whitespace-nowrap">4 févr.</span>
                  <p className="mb-0">Morningstar publie une analyse estimant la perte cumulée à 285 milliards de dollars. Les analystes commencent à distinguer le signal (réel) du produit (immature).</p>
                </div>
                <div className="flex items-start gap-4">
                  <span className="font-mono font-bold text-amber-600 whitespace-nowrap">5-7 févr.</span>
                  <p className="mb-0">Rebond partiel. Thomson Reuters regagne 4-5%. Les analystes de Spellbook publient une analyse nuancée : <em>« Was the hype overblown? »</em>.</p>
                </div>
              </div>
            </div>

            {/* Section 5 - Verdict */}
            <h2 id="buzz-justifie" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Le buzz était-il justifié ? Notre verdict d'experts en IA juridique
            </h2>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <Scale className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-amber-800 font-bold text-lg mb-2">Notre position : le signal est réel, mais la panique boursière était disproportionnée</h4>
                  <p className="text-amber-700 mb-0">
                    Claude Cowork Legal représente un <strong>changement structurel majeur</strong> dans la relation entre développeurs de modèles et éditeurs juridiques. Mais le produit lui-même, dans son état actuel de février 2026, n'est pas un remplacement crédible des solutions juridiques professionnelles. La réaction des marchés (-285 Md$) a anticipé un bouleversement futur bien plus qu'elle n'a reflété une disruption immédiate.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              Ce qui justifie l'attention (Signal : 5/5)
            </h3>

            <div className="space-y-4 my-8">
              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Rupture de la chaîne de valeur</h4>
                  <p className="text-muted-foreground mt-1">
                    Pour la première fois, un fournisseur de modèle fondamental entre directement sur le terrain des éditeurs juridiques. C'est un précédent qui change les règles du jeu pour tout l'écosystème. Si Anthropic l'a fait, OpenAI et Google suivront probablement.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Pression tarifaire structurelle</h4>
                  <p className="text-muted-foreground mt-1">
                    Les licences par siège à 500-1 500 $/mois subissent une pression sans précédent. Claude Max à 100-200$/mois offre un ratio coût/fonctionnalités qui force les éditeurs établis à justifier leur premium. Selon Morningstar, cette pression pourrait réduire les marges des éditeurs de 15-20% d'ici 2028.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">MCP comme infrastructure ouverte</h4>
                  <p className="text-muted-foreground mt-1">
                    Le <strong>Model Context Protocol</strong> (MCP) permet à Cowork de se connecter à Slack, Box, Microsoft 365, Jira — les outils utilisés quotidiennement par les directions juridiques. Cette connectivité native est un avantage considérable par rapport aux solutions juridiques cloisonnées.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold mt-10 mb-4 text-foreground">
              Ce qui ne justifie pas la panique (Produit : 2/5)
            </h3>

            <div className="space-y-4 my-8">
              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Un produit encore immature en février 2026</h4>
                  <p className="text-muted-foreground mt-1">
                    Les premiers utilisateurs rapportent que le plugin <strong>peine avec les workflows de plus de 5-6 étapes</strong>. Sur des tâches simples (revue d'un NDA standard), les résultats sont corrects. Sur des tâches complexes (analyse d'un contrat de M&A de 80 pages), la fiabilité chute significativement.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Limitations techniques majeures</h4>
                  <p className="text-muted-foreground mt-1">
                    <strong>macOS uniquement</strong> (environ 85% des grands cabinets et directions juridiques utilisent Windows). Déploiement technique requis. Questions non résolues sur la sécurité des données et le chiffrement des fichiers traités. Dans un environnement où la confidentialité client est non négociable, ces limitations sont disqualifiantes pour la majorité des organisations juridiques.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Pas de base de données juridique intégrée</h4>
                  <p className="text-muted-foreground mt-1">
                    Contrairement à Harvey (accès partiel à des bases juridiques), Lexis AI+ (fonds complet LexisNexis avec jurisprudence, doctrine et législation), Jimini AI (droit français exhaustif) ou Doctrine (jurisprudence française), Claude Cowork n'a <strong>pas accès à des bases de jurisprudence vérifiables</strong>. Le plugin travaille uniquement sur vos fichiers locaux, pas sur des sources juridiques authentifiées. C'est une limitation fondamentale pour la recherche juridique et le conseil.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg">
                <XCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-foreground">Ciblage limité : directions juridiques, pas cabinets d'avocats</h4>
                  <p className="text-muted-foreground mt-1">
                    Le plugin vise les <strong>directions juridiques d'entreprise</strong> (triage de NDA, compliance, vérification fournisseurs). Les workflows complexes des cabinets d'avocats — contentieux, M&A, restructuring, conseil stratégique — ne sont pas couverts. Comme l'a souligné <strong>Artificial Lawyer</strong> : le plugin est un <em>« legal tech ally »</em>, pas un remplacement des outils professionnels.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 6 - Comparison */}
            <h2 id="comparatif" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Claude Cowork Legal vs Harvey vs Jimini AI vs Lexis AI+ : le comparatif complet
            </h2>

            <p className="mb-6">
              Pour mettre en perspective les capacités réelles de Claude Cowork Legal, voici un comparatif détaillé avec les principales solutions d'IA juridique du marché en février 2026 :
            </p>

            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse bg-white rounded-lg overflow-hidden">
                <thead className="bg-secondary">
                  <tr>
                    <th className="p-4 text-left font-bold">Critère</th>
                    <th className="p-4 text-left font-bold">Claude Cowork Legal</th>
                    <th className="p-4 text-left font-bold">Harvey</th>
                    <th className="p-4 text-left font-bold">Jimini AI</th>
                    <th className="p-4 text-left font-bold">Lexis AI+</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Nature du produit</td>
                    <td className="p-4">Agent IA + plugin open source</td>
                    <td className="p-4">SaaS juridique spécialisé</td>
                    <td className="p-4">SaaS juridique français</td>
                    <td className="p-4">Module IA intégré à LexisNexis</td>
                  </tr>
                  <tr className="border-t bg-secondary/30">
                    <td className="p-4 font-medium">Sources juridiques intégrées</td>
                    <td className="p-4">❌ Non — fichiers locaux uniquement</td>
                    <td className="p-4">⚠️ Partiel — accès limité</td>
                    <td className="p-4">✅ Oui — droit français complet</td>
                    <td className="p-4">✅ Oui — fonds LexisNexis complet</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Revue contractuelle</td>
                    <td className="p-4">⭐⭐⭐ (basique, playbook configurable)</td>
                    <td className="p-4">⭐⭐⭐⭐⭐ (enterprise-grade)</td>
                    <td className="p-4">⭐⭐⭐⭐ (droit français)</td>
                    <td className="p-4">⭐⭐⭐ (en développement)</td>
                  </tr>
                  <tr className="border-t bg-secondary/30">
                    <td className="p-4 font-medium">Maîtrise du droit français</td>
                    <td className="p-4">⭐⭐ (connaissance générale de Claude)</td>
                    <td className="p-4">⭐⭐ (focus common law)</td>
                    <td className="p-4">⭐⭐⭐⭐⭐ (spécialisé droit français)</td>
                    <td className="p-4">⭐⭐⭐⭐⭐ (fonds Juris-Classeur, Litec)</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Autonomie agentique</td>
                    <td className="p-4">⭐⭐⭐⭐⭐ (planification multi-étapes)</td>
                    <td className="p-4">⭐⭐⭐ (workflows guidés)</td>
                    <td className="p-4">⭐⭐ (assisté)</td>
                    <td className="p-4">⭐⭐ (assisté)</td>
                  </tr>
                  <tr className="border-t bg-secondary/30">
                    <td className="p-4 font-medium">Tarif indicatif</td>
                    <td className="p-4">100-200$/mois (Claude Max)</td>
                    <td className="p-4">Enterprise (&gt;1 500$/siège/mois)</td>
                    <td className="p-4">Accessible (prix sur demande)</td>
                    <td className="p-4">Supplément abonnement LexisNexis</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-4 font-medium">Prêt pour la production</td>
                    <td className="p-4">❌ Non (février 2026)</td>
                    <td className="p-4">✅ Oui (utilisé par Am Law 100)</td>
                    <td className="p-4">✅ Oui (cabinets français)</td>
                    <td className="p-4">✅ Oui (déploiement global)</td>
                  </tr>
                  <tr className="border-t bg-secondary/30">
                    <td className="p-4 font-medium">Plateformes</td>
                    <td className="p-4">macOS uniquement</td>
                    <td className="p-4">Web (navigateur)</td>
                    <td className="p-4">Web (navigateur)</td>
                    <td className="p-4">Web (navigateur)</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-sm text-muted-foreground italic mb-8">
              Tableau comparatif établi par Gaius Legal AI en février 2026 sur la base de données publiques et d'analyses de terrain. Les tarifs sont indicatifs et peuvent varier selon les contrats enterprise.
            </p>

            {/* Section 7 */}
            <h2 id="avocats-francais" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Claude Cowork est-il adapté aux avocats et juristes français ?
            </h2>

            <p className="mb-6">
              Pour les praticiens du <strong>droit français</strong>, l'impact immédiat de Claude Cowork Legal est <strong>limité</strong>. Le plugin ne connaît pas le droit français, n'accède pas à la jurisprudence de la <strong>Cour de cassation</strong> ou du <strong>Conseil d'État</strong>, et n'est pas conçu pour les workflows spécifiques de la pratique contentieuse ou du conseil en droit continental (droit civil, droit administratif, droit des sociétés français).
            </p>

            <p className="mb-6">
              Les solutions spécialisées en droit français comme <strong>Jimini AI</strong> (accès au droit français complet), <strong>Doctrine</strong> (jurisprudence française et européenne) ou <strong>Lexis AI+</strong> (fonds Juris-Classeur, Litec, encyclopédies Dalloz) restent <strong>largement supérieures</strong> pour la pratique quotidienne du droit français.
            </p>

            <p className="mb-6">
              Cependant, le <strong>signal stratégique</strong> concerne directement les professionnels français :
            </p>

            <ul className="list-disc pl-6 space-y-3 mb-8">
              <li><strong>La pression tarifaire va s'accélérer</strong> sur tous les éditeurs juridiques, y compris français. Les solutions à 500-1 500€/mois/utilisateur devront justifier leur premium face à des alternatives génériques à 100-200€.</li>
              <li><strong>Le modèle agent-first</strong> (l'IA qui exécute des tâches de manière autonome, pas seulement qui répond à des questions) va se généraliser. Les outils juridiques français qui n'évoluent pas vers ce paradigme risquent de perdre en pertinence d'ici 2027-2028.</li>
              <li><strong>L'open source juridique</strong> émerge comme une force disruptive. Les directions juridiques les plus avancées des entreprises françaises (CAC 40, SBF 120) pourraient commencer à personnaliser des plugins open source plutôt qu'à acheter des solutions commerciales fermées.</li>
              <li><strong>Les données propriétaires restent l'avantage décisif</strong> des éditeurs spécialisés. C'est ce qu'Anthropic ne peut pas répliquer : les bases de jurisprudence authentifiées, la doctrine, les encyclopédies juridiques, les commentaires d'auteurs.</li>
            </ul>

            {/* Section 8 */}
            <h2 id="recommandation" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Notre recommandation : comment réagir à l'ère Claude Cowork en 2026
            </h2>

            <div className="bg-primary/5 border-l-4 border-primary p-6 my-10 rounded-r-lg">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-primary mt-1 mr-4 flex-shrink-0" />
                <div>
                  <h4 className="text-primary font-bold text-lg mb-2">Recommandation Gaius — Cabinet de formation IA juridique</h4>
                  <div className="text-foreground/80 space-y-3 mb-0">
                    <p><strong>Pour les directions juridiques d'entreprise :</strong> Surveillez Claude Cowork de près. Testez le plugin sur des tâches à faible risque (triage NDA, briefings internes). Ne l'utilisez pas encore pour des workflows de production critiques. Attendez la maturité du produit (version Windows, résolution des questions de sécurité des données, stabilisation des workflows multi-étapes).</p>
                    <p><strong>Pour les cabinets d'avocats :</strong> Claude Cowork n'est pas un concurrent direct — pour l'instant. Mais le paradigme agentique qu'il inaugure va transformer les attentes de vos clients d'ici 12-18 mois. Formez-vous dès maintenant aux outils d'IA juridique spécialisés qui, eux, sont prêts pour la production.</p>
                    <p><strong>Pour tous les professionnels du droit :</strong> Ne confondez pas le signal (une transformation structurelle du marché de l'IA juridique) avec le produit (un plugin encore immature). <strong>Investissez dans la montée en compétences IA de vos équipes</strong>, c'est la priorité absolue pour ne pas être dépassé par l'accélération technologique en cours.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Verdict */}
            <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Notre verdict final sur Claude Cowork Legal
            </h2>

            <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className={`w-8 h-8 ${i <= 3 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
              ))}
              <span className="ml-4 text-2xl font-bold">3/5</span>
              <span className="text-muted-foreground ml-2">(en l'état de février 2026)</span>
            </div>

            <p className="mb-6">
              <strong>Claude Cowork Legal</strong> mérite une note de <strong>3/5</strong> qui reflète un paradoxe : un <strong>signal stratégique à 5/5</strong> porté par un <strong>produit à 2/5</strong>. L'annonce a eu raison de secouer le marché : la descente d'Anthropic dans la couche applicative juridique est un événement structurel qui va remodeler l'industrie sur les 2-3 prochaines années.
            </p>

            <p className="mb-6">
              Mais le plugin lui-même, dans sa version de février 2026, n'est pas prêt pour un usage professionnel intensif. Limité à macOS, sans base juridique intégrée, avec des workflows complexes encore instables, il est aujourd'hui un <strong>prototype prometteur</strong> plus qu'un outil de production.
            </p>

            <p className="mb-8">
              La vraie question n'est pas « Claude Cowork va-t-il remplacer Harvey ou Doctrine ? ». C'est : <strong>« Comment les éditeurs juridiques vont-ils répondre à la pression structurelle qu'Anthropic vient d'initier ? »</strong>. La réponse à cette question déterminera le paysage de l'IA juridique pour les années à venir.
            </p>

            {/* Summary Box */}
            <div className="bg-primary/5 rounded-xl p-8 my-12">
              <h3 className="text-2xl font-bold mb-6 text-foreground">Récapitulatif : Claude Cowork Legal en un coup d'œil</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-green-700 mb-3 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2" /> Signal positif (5/5)
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✓ Rupture structurelle du marché de l'IA juridique</li>
                    <li>✓ Pression tarifaire bienvenue (100-200$ vs 500-1500$/mois)</li>
                    <li>✓ Paradigme agentique innovant (l'IA agit, pas seulement répond)</li>
                    <li>✓ Open source et personnalisable via GitHub</li>
                    <li>✓ Connecteurs MCP (Slack, Microsoft 365, Box, Jira)</li>
                    <li>✓ Intégration transversale multi-départements</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-red-700 mb-3 flex items-center">
                    <XCircle className="w-5 h-5 mr-2" /> Limitations actuelles (2/5)
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li>✗ Produit immature — workflows instables au-delà de 5-6 étapes</li>
                    <li>✗ macOS uniquement (85% des organisations juridiques sur Windows)</li>
                    <li>✗ Aucune base de jurisprudence ou de doctrine intégrée</li>
                    <li>✗ Sécurité et chiffrement des données non résolus</li>
                    <li>✗ Ciblé in-house counsel, pas cabinets d'avocats</li>
                    <li>✗ Pas adapté au droit français (droit continental)</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-muted-foreground">
                  <strong>À suivre de près pour :</strong> Directions juridiques d'entreprise (CAC 40, SBF 120, Fortune 500), responsables legal ops, managing partners anticipant les évolutions du marché, éditeurs juridiques réévaluant leur stratégie IA.
                </p>
              </div>
            </div>

            {/* FAQ Section */}
            <h2 id="faq" className="text-3xl font-bold mt-16 mb-6 text-foreground">
              Questions fréquentes sur Claude Cowork Legal
            </h2>

            <p className="mb-8 text-muted-foreground">
              Les réponses aux questions les plus posées par les avocats, juristes et directions juridiques sur le plugin juridique Claude Cowork d'Anthropic.
            </p>

            <div className="space-y-6 my-8">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white border rounded-lg p-6">
                  <h3 className="text-lg font-bold text-foreground flex items-start gap-3 mb-3">
                    <HelpCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    {item.question}
                  </h3>
                  <p className="text-muted-foreground ml-8">{item.answer}</p>
                </div>
              ))}
            </div>

            {/* Sources */}
            <div className="bg-secondary/30 rounded-xl p-6 my-12">
              <h4 className="font-bold text-foreground mb-4">Sources et références citées dans cet article</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• <strong>Bloomberg</strong> — « Anthropic AI Tool Sparks Selloff From Software to Broader Market » (3 février 2026)</li>
                <li>• <strong>Reuters</strong> — « Anthropic's new AI tools deepen selloff in data analytics and software stocks » (3 février 2026)</li>
                <li>• <strong>The Guardian</strong> — « Anthropic's Launch of AI Legal Tool Hits Shares in European Data Companies » (3 février 2026)</li>
                <li>• <strong>Morningstar</strong> (Ali Mogharabi) — « Thomson Reuters, RELX, and Wolters Stocks Crushed After Anthropic Debuts Claude Legal Plug-In » (4 février 2026)</li>
                <li>• <strong>LawNext</strong> (Robert Ambrogi) — « Anthropic's Legal Plugin for Claude Cowork May Be the Opening Salvo » (février 2026)</li>
                <li>• <strong>Artificial Lawyer</strong> — « Claude Cowork – Legal Tech Ally, Not Legal Tech Killer » (17 février 2026)</li>
                <li>• <strong>Reed Smith LLP</strong> — « Claude Cowork legal plugin – why I'm paying attention » (16 février 2026)</li>
                <li>• <strong>Spellbook</strong> — « Claude's Legal Plugin Sparks $285B Selloff — But Was The Hype Overblown? » (février 2026)</li>
                <li>• <strong>Anthropic</strong> — Annonce officielle Claude Cowork (12 janvier 2026) et dépôt GitHub des plugins (30 janvier 2026)</li>
              </ul>
            </div>

          </div>

          {/* CTA Section */}
          <div className="mt-16 pt-8 border-t">
            <h3 className="text-2xl font-bold mb-6">Formations recommandées</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Brain className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Fondamentaux de l'IA Juridique</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Comprenez les outils d'IA juridique du marché — Claude, Harvey, Jimini, Lexis AI+ — et choisissez ceux adaptés à votre pratique.
                  </p>
                  <Button asChild>
                    <Link to="/training">
                      Découvrir <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              <Card className="bg-white">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Shield className="w-6 h-6 text-primary" />
                    <h4 className="font-bold">Audit IA Cabinet</h4>
                  </div>
                  <p className="text-muted-foreground mb-4">
                    Évaluez votre maturité IA et identifiez les outils les plus adaptés à votre cabinet ou direction juridique.
                  </p>
                  <Button asChild variant="outline">
                    <Link to="/audit">
                      En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // English version
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords="claude cowork legal, what is claude cowork legal, anthropic legal plugin, claude cowork lawyer, claude cowork review, claude cowork price, claude cowork features, legal ai anthropic, cowork legal plugin, claude legal analysis, ai lawyers 2026, anthropic legal tech, claude cowork vs harvey, claude cowork vs lexis, claude cowork stock market, thomson reuters claude, mcp claude cowork, claude cowork test"
        canonicalUrl="https://gaius-tech.com/articles/claude-cowork-legal"
        alternateUrlFr="https://gaius-tech.com/articles/claude-cowork-juridique"
        alternateUrlEn="https://gaius-tech.com/articles/claude-cowork-legal"
        structuredData={structuredDataCombined}
        pageType="article"
      />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">Expert Analysis</Badge>
              <Badge variant="secondary" className="bg-red-100 text-red-800 flex items-center gap-1">
                <TrendingDown className="w-3 h-3" />
                -$285B Market Impact
              </Badge>
              <Badge variant="secondary" className="bg-amber-100 text-amber-800">February 2026</Badge>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Claude Cowork Legal: The Anthropic Plugin That Wiped $285 Billion Off the Markets
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Thomson Reuters: -16% (≈$75B market cap lost). Wolters Kluwer: -10%. RELX: -9%. Was the panic justified? Our complete expert analysis.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <span className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>February 18, 2026</span>
              </span>
              <span className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>25 min read</span>
              </span>
              <span className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span>Gaius Team — Legal AI Training</span>
              </span>
            </div>
          </div>
          <img
            src="https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&h=400&fit=crop"
            alt="Claude Cowork Legal - Analysis of the $285 billion market impact on Thomson Reuters, RELX and Wolters Kluwer"
            className="w-full h-64 object-cover rounded-lg"
          />
        </div>
      </div>

      {/* Quick Answer Box */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
          <div className="flex items-start gap-3 mb-3">
            <MessageSquare className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
            <h2 className="font-bold text-lg text-foreground">In brief: What is Claude Cowork Legal?</h2>
          </div>
          <p className="text-foreground/80 mb-3">
            <strong>Claude Cowork Legal</strong> is an open-source legal plugin launched on January 30, 2026 by <strong>Anthropic</strong> for its Claude Cowork platform. It automates contract review, NDA triage, and vendor checks for in-house legal teams. Its launch triggered a <strong>$285 billion stock market crash</strong> (Thomson Reuters -16%, source: Bloomberg, February 3, 2026). <strong>Our verdict: strategic signal 5/5, current product 2/5, overall score 3/5.</strong>
          </p>
          <p className="text-sm text-muted-foreground">
            Analysis by <a href="https://gaius-tech.com" className="text-primary hover:underline">Gaius Legal AI</a> — Legal AI training and consulting for lawyers and legal professionals.
          </p>
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">

          <p className="text-xl leading-relaxed mb-8">
            On January 30, 2026, <strong>Anthropic</strong> — the company behind <strong>Claude</strong> — launched a legal plugin for its new <strong>Claude Cowork</strong> platform. What could have been a routine product update triggered an unprecedented market shock in the legal information industry. According to <strong>Bloomberg</strong> (February 3, 2026), <strong>Thomson Reuters fell 16%, Wolters Kluwer 10%, RELX 9%</strong>. Over <strong>$285 billion in market capitalization</strong> evaporated in a single week. What actually happened? And was the buzz justified?
          </p>

          <div className="bg-red-50 border-l-4 border-red-400 p-6 my-10 rounded-r-lg">
            <div className="flex items-start">
              <TrendingDown className="w-6 h-6 text-red-500 mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-red-800 font-bold text-lg mb-2">Market Impact at a Glance</h4>
                <ul className="text-red-700 space-y-1 mb-0">
                  <li>• <strong>Thomson Reuters (TRI)</strong>: -16.03% on February 3, 2026 (≈$75B market cap lost)</li>
                  <li>• <strong>Wolters Kluwer (WKL)</strong>: -10.2% on Euronext Amsterdam</li>
                  <li>• <strong>RELX (REL)</strong>: -9.1% on the London Stock Exchange</li>
                  <li>• <strong>Total estimated</strong>: $285 billion in combined market cap decline over 5 days</li>
                </ul>
                <p className="text-red-600 text-sm mt-3 mb-0">
                  <em>Sources: Bloomberg, Reuters, The Guardian — February 3, 2026. Morningstar — February 4, 2026.</em>
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            What Is Claude Cowork? Understanding the Platform Behind the Plugin
          </h2>

          <p className="mb-6">
            Launched on January 12, 2026, Cowork extends <strong>Claude Code</strong> beyond developers. Positioned as <em>"Claude Code for the rest of your work"</em> (source: Anthropic official announcement), it runs locally on your computer, accesses a workspace folder, and can <strong>read, edit, and create files</strong> while planning and executing multi-step tasks autonomously in a sandboxed virtual machine. It requires a Claude Max subscription ($100-200/month) and currently runs on macOS only.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            What Does the Claude Cowork Legal Plugin Actually Do?
          </h2>

          <p className="mb-6">
            The legal plugin targets <strong>in-house legal teams</strong> with 5 core features: <strong>/review-contract</strong> (clause-by-clause review against configurable playbooks with green/yellow/red flags), <strong>/triage-nda</strong> (automatic NDA triage into 3 categories), <strong>/vendor-check</strong> (vendor agreement portfolio checks), <strong>/respond</strong> (templated responses for DSARs and discovery holds), and <strong>/brief</strong> (automated contextual briefings). Anthropic explicitly frames it as assistance: <em>"All outputs should be reviewed by licensed attorneys"</em> (source: GitHub README, January 30, 2026).
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Why Did Claude Cowork Legal Cause Such Massive Buzz?
          </h2>

          <p className="mb-6">
            <strong>Factor #1 — Value chain disruption:</strong> For the first time, a foundation model developer (Anthropic) packaged legal workflow products directly into its platform, bypassing intermediaries like Thomson Reuters and LexisNexis. As <strong>Robert Ambrogi wrote on LawNext</strong>: <em>"This may be the opening salvo in a war that reshapes the legal tech industry."</em>
          </p>

          <p className="mb-6">
            <strong>Factor #2 — Pricing frustration:</strong> Enterprise legal AI tools charge $500-1,500+/seat/month. Claude Max at $100-200/month was perceived as an inevitable pricing correction. Morningstar analyst <strong>Ali Mogharabi</strong> called it <em>"pricing pressure that had been building for years."</em>
          </p>

          <p className="mb-6">
            <strong>Factor #3 — Cross-department integration:</strong> The legal plugin is part of an 11-plugin suite covering sales, marketing, finance, and support. <strong>Reed Smith</strong> noted: <em>"The cross-departmental integration is what makes this genuinely different from existing legal tech"</em> (February 16, 2026).
          </p>

          <p className="mb-8">
            <strong>Factor #4 — Open source distribution:</strong> All plugins are published on GitHub as customizable templates, threatening the per-seat licensing model. <strong>Artificial Lawyer</strong> called it <em>"a Trojan horse for enterprise legal departments"</em> (February 17, 2026).
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Was the Claude Cowork Legal Hype Justified? Our Expert Verdict
          </h2>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-6 my-10 rounded-r-lg">
            <div className="flex items-start">
              <Scale className="w-6 h-6 text-amber-600 mt-1 mr-4 flex-shrink-0" />
              <div>
                <h4 className="text-amber-800 font-bold text-lg mb-2">The signal is real. The $285B panic was disproportionate.</h4>
                <p className="text-amber-700 mb-0">
                  Claude Cowork Legal represents a <strong>major structural shift</strong> in the relationship between model developers and legal tech vendors. But the product itself is not yet a credible replacement for professional legal solutions. The market reaction anticipated a future disruption far more than it reflected an immediate one.
                </p>
              </div>
            </div>
          </div>

          <p className="mb-6">
            <strong>Key limitations (Product: 2/5):</strong> macOS only (≈85% of legal organizations run Windows), no integrated legal databases, struggles with workflows beyond 5-6 steps, unresolved data security questions, targets in-house teams rather than law firms. Not suitable for French law practice.
          </p>

          <p className="mb-6">
            <strong>What matters (Signal: 5/5):</strong> The precedent of a foundation model company entering the legal vertical directly, the open-source distribution model, MCP connectivity to enterprise tools (Slack, M365, Box), and structural pricing pressure on incumbents that Morningstar estimates could reduce vendor margins by 15-20% by 2028.
          </p>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Claude Cowork vs Harvey vs Jimini AI vs Lexis AI+: Comparison
          </h2>

          <div className="overflow-x-auto my-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden">
              <thead className="bg-secondary">
                <tr>
                  <th className="p-4 text-left font-bold">Criteria</th>
                  <th className="p-4 text-left font-bold">Claude Cowork</th>
                  <th className="p-4 text-left font-bold">Harvey</th>
                  <th className="p-4 text-left font-bold">Jimini AI</th>
                  <th className="p-4 text-left font-bold">Lexis AI+</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="p-4 font-medium">Legal databases</td>
                  <td className="p-4">❌ None — local files only</td>
                  <td className="p-4">⚠️ Partial</td>
                  <td className="p-4">✅ Full (French law)</td>
                  <td className="p-4">✅ Full (LexisNexis)</td>
                </tr>
                <tr className="border-t bg-secondary/30">
                  <td className="p-4 font-medium">Agentic autonomy</td>
                  <td className="p-4">⭐⭐⭐⭐⭐</td>
                  <td className="p-4">⭐⭐⭐</td>
                  <td className="p-4">⭐⭐</td>
                  <td className="p-4">⭐⭐</td>
                </tr>
                <tr className="border-t">
                  <td className="p-4 font-medium">Pricing</td>
                  <td className="p-4">$100-200/mo</td>
                  <td className="p-4">&gt;$1,500/seat/mo</td>
                  <td className="p-4">Affordable</td>
                  <td className="p-4">Add-on</td>
                </tr>
                <tr className="border-t bg-secondary/30">
                  <td className="p-4 font-medium">Production-ready</td>
                  <td className="p-4">❌ Not yet</td>
                  <td className="p-4">✅ Am Law 100</td>
                  <td className="p-4">✅ Yes</td>
                  <td className="p-4">✅ Global</td>
                </tr>
                <tr className="border-t">
                  <td className="p-4 font-medium">Platform</td>
                  <td className="p-4">macOS only</td>
                  <td className="p-4">Web browser</td>
                  <td className="p-4">Web browser</td>
                  <td className="p-4">Web browser</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Our Final Verdict on Claude Cowork Legal
          </h2>

          <div className="flex items-center space-x-3 my-6 p-4 bg-secondary/30 rounded-lg">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} className={`w-8 h-8 ${i <= 3 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
            ))}
            <span className="ml-4 text-2xl font-bold">3/5</span>
            <span className="text-muted-foreground ml-2">(February 2026)</span>
          </div>

          <p className="mb-8">
            A <strong>5/5 strategic signal</strong> carried by a <strong>2/5 product</strong>. The announcement rightly shook the market — Anthropic entering the legal application layer is a structural event. But the plugin itself is a <strong>promising prototype, not a production tool</strong>. The real question: how will Thomson Reuters, LexisNexis, and Wolters Kluwer respond to the structural pressure Anthropic just initiated?
          </p>

          {/* FAQ Section */}
          <h2 className="text-3xl font-bold mt-16 mb-6 text-foreground">
            Frequently Asked Questions About Claude Cowork Legal
          </h2>

          <div className="space-y-6 my-8">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white border rounded-lg p-6">
                <h3 className="text-lg font-bold text-foreground flex items-start gap-3 mb-3">
                  <HelpCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  {item.question}
                </h3>
                <p className="text-muted-foreground ml-8">{item.answer}</p>
              </div>
            ))}
          </div>

          {/* Sources */}
          <div className="bg-secondary/30 rounded-xl p-6 my-12">
            <h4 className="font-bold text-foreground mb-4">Sources & References</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• <strong>Bloomberg</strong> — "Anthropic AI Tool Sparks Selloff From Software to Broader Market" (Feb 3, 2026)</li>
              <li>• <strong>Reuters</strong> — "Anthropic's new AI tools deepen selloff in data analytics stocks" (Feb 3, 2026)</li>
              <li>• <strong>The Guardian</strong> — "Anthropic's Launch of AI Legal Tool Hits Shares in European Data Companies" (Feb 3, 2026)</li>
              <li>• <strong>Morningstar</strong> — "Thomson Reuters, RELX, and Wolters Stocks Crushed" (Feb 4, 2026)</li>
              <li>• <strong>LawNext</strong> (Robert Ambrogi) — "Anthropic's Legal Plugin May Be the Opening Salvo" (Feb 2026)</li>
              <li>• <strong>Artificial Lawyer</strong> — "Claude Cowork – Legal Tech Ally" (Feb 17, 2026)</li>
              <li>• <strong>Reed Smith LLP</strong> — "Claude Cowork legal plugin – why I'm paying attention" (Feb 16, 2026)</li>
              <li>• <strong>Anthropic</strong> — Official Claude Cowork announcement (Jan 12, 2026) and GitHub plugin repository (Jan 30, 2026)</li>
            </ul>
          </div>

        </div>

        {/* CTA Section */}
        <div className="mt-16 pt-8 border-t">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild>
              <Link to="/training">
                Discover our training programs <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/blog">
                Read our other articles
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
